/**
 * Battle intro overlay - Shows player names sliding in with VS in the center
 * Similar to Clash Royale's battle intro animation
 */
export class BattleIntroOverlay {
  private container: HTMLElement;
  private overlay: HTMLElement | null = null;
  private onAnimationComplete: () => void;

  constructor(container: HTMLElement, onAnimationComplete: () => void) {
    this.container = container;
    this.onAnimationComplete = onAnimationComplete;
    this.createOverlay();
  }

  /**
   * Create the overlay element with styles
   */
  private createOverlay(): void {
    this.overlay = document.createElement('div');
    this.overlay.className = 'battle-intro-overlay';
    this.overlay.innerHTML = `
      <style>
        .battle-intro-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0);
          display: none;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          pointer-events: none;
        }
        .battle-intro-overlay.visible {
          display: flex;
        }
        .battle-intro-overlay.fade-in {
          animation: bio-fade-in 0.3s ease-out forwards;
        }
        .battle-intro-overlay.fade-out {
          animation: bio-fade-out 0.3s ease-out forwards;
        }
        @keyframes bio-fade-in {
          from { background: rgba(0, 0, 0, 0); }
          to { background: rgba(0, 0, 0, 0.85); }
        }
        @keyframes bio-fade-out {
          from { background: rgba(0, 0, 0, 0.85); }
          to { background: rgba(0, 0, 0, 0); }
        }

        .battle-intro-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 20px;
          width: 100%;
          max-width: 900px;
          padding: 0 20px;
        }

        .battle-intro-player {
          font-size: 36px;
          font-weight: bold;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          text-transform: uppercase;
          letter-spacing: 2px;
          opacity: 0;
          text-shadow: 0 0 20px currentColor, 0 0 40px currentColor;
          text-align: center;
          word-break: break-word;
          max-width: 100%;
        }

        @media (min-width: 768px) {
          .battle-intro-content {
            flex-direction: row;
            gap: 40px;
          }
          .battle-intro-player {
            font-size: 42px;
          }
        }

        .battle-intro-player.p1 {
          color: #3b82f6;
          transform: translateY(-100vh);
        }
        .battle-intro-player.p1.animate {
          animation: bio-slide-in-top 0.5s ease-out forwards;
        }

        .battle-intro-player.p2 {
          color: #ef4444;
          transform: translateY(100vh);
        }
        .battle-intro-player.p2.animate {
          animation: bio-slide-in-bottom 0.5s ease-out forwards;
        }

        @keyframes bio-slide-in-top {
          from {
            transform: translateY(-100vh);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes bio-slide-in-bottom {
          from {
            transform: translateY(100vh);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @media (min-width: 768px) {
          .battle-intro-player.p1 {
            transform: translateX(-100vw);
          }
          .battle-intro-player.p1.animate {
            animation: bio-slide-in-left 0.5s ease-out forwards;
          }
          .battle-intro-player.p2 {
            transform: translateX(100vw);
          }
          .battle-intro-player.p2.animate {
            animation: bio-slide-in-right 0.5s ease-out forwards;
          }
        }

        @keyframes bio-slide-in-left {
          from {
            transform: translateX(-100vw);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes bio-slide-in-right {
          from {
            transform: translateX(100vw);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .battle-intro-vs {
          font-size: 48px;
          font-weight: bold;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          color: #ffd700;
          text-shadow: 0 0 30px rgba(255, 215, 0, 0.8), 0 0 60px rgba(255, 215, 0, 0.5);
          transform: scale(0);
          opacity: 0;
        }
        .battle-intro-vs.animate {
          animation: bio-vs-appear 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
          animation-delay: 0.3s;
        }

        @media (min-width: 768px) {
          .battle-intro-vs {
            font-size: 72px;
          }
        }

        @keyframes bio-vs-appear {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.3);
            opacity: 1;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
      </style>
      <div class="battle-intro-content">
        <div class="battle-intro-player p1" id="bio-player1">Player 1</div>
        <div class="battle-intro-vs" id="bio-vs">VS</div>
        <div class="battle-intro-player p2" id="bio-player2">Player 2</div>
      </div>
    `;
    this.container.appendChild(this.overlay);
  }

  /**
   * Show the battle intro animation
   */
  show(player1Name: string, player2Name: string, player1Color?: string, player2Color?: string): void {
    console.log('[BattleIntroOverlay] show called:', player1Name, 'vs', player2Name);
    if (!this.overlay) {
      console.log('[BattleIntroOverlay] overlay is null!');
      return;
    }

    const p1El = this.overlay.querySelector('#bio-player1') as HTMLElement;
    const p2El = this.overlay.querySelector('#bio-player2') as HTMLElement;
    const vsEl = this.overlay.querySelector('#bio-vs') as HTMLElement;

    if (p1El) {
      p1El.textContent = player1Name;
      if (player1Color) p1El.style.color = player1Color;
      p1El.classList.remove('animate');
    }
    if (p2El) {
      p2El.textContent = player2Name;
      if (player2Color) p2El.style.color = player2Color;
      p2El.classList.remove('animate');
    }
    if (vsEl) {
      vsEl.classList.remove('animate');
    }

    // Reset overlay state
    this.overlay.classList.remove('fade-out');
    this.overlay.classList.add('visible', 'fade-in');

    // Start animations after a brief delay to ensure DOM update
    requestAnimationFrame(() => {
      if (p1El) p1El.classList.add('animate');
      if (p2El) p2El.classList.add('animate');
      if (vsEl) vsEl.classList.add('animate');
    });

    // Hold for display time then fade out
    // Timeline: fade-in (0.3s) + slide-in (0.5s) + vs-appear (0.3s delay + 0.6s) + hold (0.5s) = ~2.2s
    setTimeout(() => {
      this.fadeOut();
    }, 1700);
  }

  /**
   * Fade out the overlay and trigger completion callback
   */
  private fadeOut(): void {
    if (!this.overlay) return;

    this.overlay.classList.remove('fade-in');
    this.overlay.classList.add('fade-out');

    // Remove after fade out animation
    setTimeout(() => {
      if (this.overlay) {
        this.overlay.classList.remove('visible', 'fade-out');
        // Reset animation classes for next use
        const p1El = this.overlay.querySelector('#bio-player1');
        const p2El = this.overlay.querySelector('#bio-player2');
        const vsEl = this.overlay.querySelector('#bio-vs');
        if (p1El) p1El.classList.remove('animate');
        if (p2El) p2El.classList.remove('animate');
        if (vsEl) vsEl.classList.remove('animate');
      }
      this.onAnimationComplete();
    }, 300);
  }

  /**
   * Clean up the overlay
   */
  dispose(): void {
    if (this.overlay && this.overlay.parentNode) {
      this.overlay.parentNode.removeChild(this.overlay);
    }
    this.overlay = null;
  }
}
